(ns metabase.lib.schema.metadata.fingerprint
  "Schemas for the `fingerprint` a Field carries: the summary statistics the analysis stage computes from a sample of
  its values.

  Every map in here is deliberately open. A fingerprint is not something anybody sends us -- it is written by sync,
  stored in the app DB, and read back by whatever version happens to be running later, so a row can easily hold stats
  an older (or newer) version of this schema does not name, and rejecting one would make the whole column metadata map
  it hangs off of invalid. It is provider content in the same sense `:metabase.lib.schema.metadata/column` is, and is
  covered by the same `:lib/metadata` exception. Rather than a `:map-of`, the statistics we do know about are named
  and typed, so a fingerprint with a nonsense *value* is still rejected."
  (:require
   [metabase.lib.schema.common :as lib.schema.common]
   [metabase.util.malli.registry :as mr]
   [metabase.util.performance :as perf]))

(mr/def ::percent
  "Schema for something represting a percentage. A floating-point value between (inclusive) 0 and 1."
  [:and
   number?
   [:fn
    {:error/message "Valid percentage between (inclusive) 0 and 1."}
    #(<= 0 % 1)]])

(mr/def ::fingerprint.global
  "Fingerprint values that Fields of all types should have."
  [:map
   {:closed true, :decode/normalize lib.schema.common/normalize-map-no-kebab-case}
   [:distinct-count {:optional true} :int]
   [:nil%           {:optional true} [:maybe [:ref ::percent]]]])

(mr/def ::fingerprint.number
  "Schema for fingerprint information for Fields deriving from `:type/Number`."
  [:map
   {:closed true, :decode/normalize lib.schema.common/normalize-map-no-kebab-case}
   [:min             {:optional true} [:maybe number?]]
   [:max             {:optional true} [:maybe number?]]
   [:avg             {:optional true} [:maybe number?]]
   [:q1              {:optional true} [:maybe number?]]
   [:q3              {:optional true} [:maybe number?]]
   [:sd              {:optional true} [:maybe number?]]
   [:skewness        {:optional true} [:maybe number?]]
   [:excess-kurtosis {:optional true} [:maybe number?]]
   [:mode-fraction   {:optional true} [:maybe [:ref ::percent]]]
   [:top-3-fraction  {:optional true} [:maybe [:ref ::percent]]]
   [:zero-fraction   {:optional true} [:maybe [:ref ::percent]]]])

(mr/def ::fingerprint.text
  "Schema for fingerprint information for Fields deriving from `:type/Text`."
  [:map
   {:closed true, :decode/normalize lib.schema.common/normalize-map-no-kebab-case}
   [:percent-json   {:optional true} [:maybe [:ref ::percent]]]
   [:percent-url    {:optional true} [:maybe [:ref ::percent]]]
   [:percent-email  {:optional true} [:maybe [:ref ::percent]]]
   [:percent-state  {:optional true} [:maybe [:ref ::percent]]]
   [:percent-blank  {:optional true} [:maybe [:ref ::percent]]]
   [:average-length {:optional true} [:maybe number?]]
   [:min-length     {:optional true} [:maybe number?]]
   [:max-length     {:optional true} [:maybe number?]]
   [:mode-fraction  {:optional true} [:maybe [:ref ::percent]]]
   [:top-3-fraction {:optional true} [:maybe [:ref ::percent]]]])

(mr/def ::fingerprint.temporal
  "Schema for fingerprint information for Fields deriving from `:type/Temporal`."
  [:map
   {:closed true, :decode/normalize lib.schema.common/normalize-map-no-kebab-case}
   [:earliest             {:optional true} [:maybe :string]]
   [:latest               {:optional true} [:maybe :string]]
   [:skewness             {:optional true} [:maybe number?]]
   [:weekday-distribution {:optional true} [:maybe [:sequential [:ref ::percent]]]]
   [:hour-distribution    {:optional true} [:maybe [:sequential [:ref ::percent]]]]
   [:mode-fraction        {:optional true} [:maybe [:ref ::percent]]]
   [:top-3-fraction       {:optional true} [:maybe [:ref ::percent]]]])

(mr/def ::fingerprint.type-specific
  "Schema for type-specific fingerprint information."
  [:and
   ;; the keys are base types, and prod has fingerprints stored under lower-cased ones (#63397), so normalize them
   ;; here -- a `[:map-of ::lib.schema.common/base-type ...]` conjunct would do it too, but `:map-of` silently drops
   ;; the entries it can't normalize instead of surfacing them
   {:decode/normalize (fn [m]
                        (some-> (lib.schema.common/normalize-map-no-kebab-case m)
                                (perf/update-keys lib.schema.common/normalize-base-type)))}
   [:map {:closed true}
    [:type/Number   {:optional true} [:ref ::fingerprint.number]]
    [:type/Text     {:optional true} [:ref ::fingerprint.text]]
    ;; temporal fingerprints are keyed by `:type/DateTime` for historical reasons. `DateTime` used to be the parent of
    ;; all temporal MB types.
    [:type/DateTime {:optional true} [:ref ::fingerprint.temporal]]
    [:type/Date     {:optional true} [:ref ::fingerprint.temporal]]
    [:type/Time     {:optional true} [:ref ::fingerprint.temporal]]]
   [:fn
    {:error/message "Type-specific fingerprint with exactly one key"}
    (fn [m]
      (= 1 (count (keys m))))]])

(mr/def ::fingerprint
  "Schema for a Field 'fingerprint' generated as part of the analysis stage. Used to power the 'classification'
   sub-stage of analysis. Stored as the `fingerprint` column of Field."
  [:map
   {:closed true, :decode/normalize lib.schema.common/normalize-map-no-kebab-case}
   [:global       {:optional true} [:ref ::fingerprint.global]]
   [:type         {:optional true} [:ref ::fingerprint.type-specific]]
   [:experimental {:optional true} [:map {:closed true}]]])
